import React from 'react';
import { motion } from 'framer-motion';
import { Palette, Layers, Type, ChevronRight, RotateCw, Maximize2, Save, BoxIcon } from 'lucide-react';
interface BuilderVisualProps {
  theme?: 'light' | 'dark';
}
// A custom, in-brand 3D uniform builder visual.
// NO third-party branding, NO fake URLs, NO browser chrome.
export const BuilderVisual = ({
  theme = 'dark'
}: BuilderVisualProps) => {
  const isDark = theme === 'dark';
  return <div className={`relative rounded-2xl overflow-hidden shadow-2xl border w-full ${isDark ? 'border-white/10 bg-[#0F0F12]' : 'border-gray200 bg-white'}`} data-id="element-16">
      {/* App toolbar */}
      <div className={`px-5 py-3.5 flex items-center justify-between border-b ${isDark ? 'bg-[#16161B] border-white/10' : 'bg-gray50 border-gray200'}`} data-id="element-17">
        <div className="flex items-center gap-3 min-w-0" data-id="element-18">
          <div className="w-8 h-8 rounded-md bg-brandRed flex items-center justify-center shrink-0" data-id="element-19">
            <div className="w-4 h-4 text-white" strokeWidth={2.5} data-id="element-20" />
          </div>
          <div className={`text-sm font-bold ${isDark ? 'text-white' : 'text-ink'}`} data-id="element-21">
            Kit Builder
          </div>
          <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'} hidden sm:block truncate`} data-id="element-22">
            / Baseball / Full Button Jersey
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0" data-id="element-23">
          <button className={`p-1.5 rounded ${isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray100 text-gray-500'}`} data-id="element-24">
            <RotateCw className="w-4 h-4" data-id="element-25" />
          </button>
          <button className={`p-1.5 rounded ${isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray100 text-gray-500'}`} data-id="element-26">
            <Maximize2 className="w-4 h-4" data-id="element-27" />
          </button>
          <button className="bg-brandRed text-white text-xs font-bold py-1.5 px-3 rounded flex items-center gap-1.5 ml-1" data-id="element-28">
            <Save className="w-3 h-3" data-id="element-29" /> Save
          </button>
        </div>
      </div>

      {/* Main builder area */}
      <div className="grid grid-cols-12 h-[360px] md:h-[420px]" data-id="element-30">
        {/* Left sidebar */}
        <div className={`col-span-2 lg:col-span-2 border-r ${isDark ? 'border-white/10 bg-[#0B0B0F]' : 'border-gray200 bg-gray50'} py-4 flex flex-col items-center gap-2`} data-id="element-31">
          {[{
          icon: Palette,
          active: true,
          label: 'Color'
        }, {
          icon: Layers,
          active: false,
          label: 'Pattern'
        }, {
          icon: Type,
          active: false,
          label: 'Text'
        }, {
          icon: BoxIcon,
          active: false,
          label: 'Fabric'
        }].map((tool, i) => <button key={i} className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${tool.active ? 'bg-brandRed text-white' : isDark ? 'text-gray-400 hover:bg-white/10' : 'text-gray-500 hover:bg-gray100'}`} title={tool.label} data-id="element-32">
              <tool.icon className="w-5 h-5" data-id="element-33" />
            </button>)}
        </div>

        {/* Center — 3D preview */}
        <div className={`col-span-7 relative ${isDark ? 'bg-gradient-to-b from-[#1A1A20] to-[#0F0F12]' : 'bg-gradient-to-b from-white to-gray50'} flex items-center justify-center p-6 overflow-hidden`} data-id="element-34">
          <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: isDark ? 'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)' : 'linear-gradient(rgba(0,0,0,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.04) 1px, transparent 1px)',
          backgroundSize: '24px 24px'
        }} data-id="element-35"></div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="relative z-10" data-id="element-36">
            <JerseyMockup data-id="element-37" />
          </motion.div>

          <div className={`absolute top-4 left-4 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${isDark ? 'bg-white/10 text-white backdrop-blur-sm' : 'bg-ink text-white'}`} data-id="element-38">
            3D Preview
          </div>
          <div className={`absolute bottom-4 right-4 px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-2 ${isDark ? 'bg-white/10 text-gray-300 backdrop-blur-sm' : 'bg-white text-charcoal border border-gray200'}`} data-id="element-39">
            <div className="w-2 h-2 rounded-full bg-green-500" data-id="element-40"></div>
            Live render
          </div>
        </div>

        {/* Right sidebar — color & options */}
        <div className={`col-span-3 border-l ${isDark ? 'border-white/10 bg-[#0B0B0F]' : 'border-gray200 bg-gray50'} p-3 md:p-4 flex flex-col gap-4`} data-id="element-41">
          <div data-id="element-42">
            <div className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-43">
              Primary Color
            </div>
            <div className="grid grid-cols-4 gap-1.5" data-id="element-44">
              {['#D71920', '#0B0B0F', '#FFFFFF', '#1E3A8A', '#FBBF24', '#16A34A', '#7C3AED', '#EC4899'].map((color, i) => <button key={i} className={`aspect-square rounded ${i === 0 ? 'ring-2 ring-offset-1 ring-brandRed ' + (isDark ? 'ring-offset-[#0B0B0F]' : 'ring-offset-gray50') : ''}`} style={{
              backgroundColor: color,
              border: color === '#FFFFFF' ? '1px solid #E5E7EB' : 'none'
            }} data-id="element-45" />)}
            </div>
          </div>

          <div data-id="element-46">
            <div className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-47">
              Pattern
            </div>
            <div className="grid grid-cols-3 gap-1.5" data-id="element-48">
              {[0, 1, 2].map(i => <div key={i} className={`aspect-square rounded border overflow-hidden ${i === 0 ? 'border-brandRed' : isDark ? 'border-white/10' : 'border-gray200'} ${isDark ? 'bg-white/5' : 'bg-white'}`} data-id="element-49">
                  {i === 0 && <div className="w-full h-full bg-brandRed/80" data-id="element-50"></div>}
                  {i === 1 && <div className="w-full h-full flex" data-id="element-51">
                      <div className="flex-1 bg-brandRed/80" data-id="element-52"></div>
                      <div className="flex-1 bg-white" data-id="element-53"></div>
                      <div className="flex-1 bg-brandRed/80" data-id="element-54"></div>
                    </div>}
                  {i === 2 && <div className="w-full h-full" style={{
                background: 'linear-gradient(to bottom, #D71920, #0B0B0F)'
              }} data-id="element-55"></div>}
                </div>)}
            </div>
          </div>

          <div className="hidden lg:block" data-id="element-56">
            <div className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-57">
              Fabric
            </div>
            <div className={`text-xs ${isDark ? 'text-gray-300' : 'text-charcoal'} font-semibold border ${isDark ? 'border-white/10 bg-white/5' : 'border-gray200 bg-white'} rounded px-2.5 py-2 flex items-center justify-between`} data-id="element-58">
              <span data-id="element-59">Polydex</span>
              <ChevronRight className="w-3 h-3 opacity-50" data-id="element-60" />
            </div>
          </div>
        </div>
      </div>
    </div>;
};
const JerseyMockup = () => <svg viewBox="0 0 200 240" className="w-44 h-52 md:w-56 md:h-64 drop-shadow-2xl" data-id="element-61">
    <defs data-id="element-62">
      <linearGradient id="jerseyBody" x1="0%" y1="0%" x2="0%" y2="100%" data-id="element-63">
        <stop offset="0%" stopColor="#E91E27" data-id="element-64" />
        <stop offset="100%" stopColor="#A8141A" data-id="element-65" />
      </linearGradient>
      <linearGradient id="jerseyShadow" x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-66">
        <stop offset="0%" stopColor="rgba(0,0,0,0.2)" data-id="element-67" />
        <stop offset="100%" stopColor="rgba(0,0,0,0)" data-id="element-68" />
      </linearGradient>
    </defs>
    <path d="M50 40 L80 25 L100 35 L120 25 L150 40 L165 80 L150 90 L150 200 Q150 215 135 215 L65 215 Q50 215 50 200 L50 90 L35 80 Z" fill="url(#jerseyBody)" stroke="#0B0B0F" strokeWidth="1.5" data-id="element-69" />
    <path d="M50 40 L80 25 L100 35 L120 25 L150 40 L165 80 L150 90 L150 200 Q150 215 135 215 L65 215 Q50 215 50 200 L50 90 L35 80 Z" fill="url(#jerseyShadow)" opacity="0.3" data-id="element-70" />
    <path d="M80 25 L100 35 L120 25 L120 50 L100 55 L80 50 Z" fill="#0B0B0F" data-id="element-71" />
    <text x="100" y="135" textAnchor="middle" fontFamily="Archivo, sans-serif" fontWeight="900" fontSize="56" fill="#FFFFFF" data-id="element-72">
      07
    </text>
    <line x1="50" y1="200" x2="150" y2="200" stroke="#0B0B0F" strokeWidth="0.5" opacity="0.4" data-id="element-73" />
  </svg>;